import React, { useEffect, useMemo, useRef, useState } from 'react';

import { Empty, Spin, Typography } from 'antd';
import { useDebounce } from 'src/hooks/useDebounce';
import { usePostReportPreviewMutation } from 'src/redux/services/centralApi';
import type { ReportBlock, ReportJoin } from 'src/interfaces/entities/observer';

// ponytail: o preview manda no máximo 500 linhas por consulta (é só amostra)
const MAX_PREVIEW_ROWS = 500;

interface IReportPreviewComponent {
	title: string
	blocks: ReportBlock[]
	joins?: ReportJoin[]
	/** Amostra do teste de SQL por fonte (key da consulta, ou 'resultado' no observador) */
	data: Record<string, Record<string, unknown>[]>
}

// Mesmo HTML do anexo enviado no WhatsApp, gerado pela API e mostrado num iframe
export default function ReportPreview({ title, blocks, joins, data, }: IReportPreviewComponent) {
	const [ postPreview, { isLoading, },] = usePostReportPreviewMutation();
	const [ html, setHtml, ] = useState<string>();
	const [ error, setError, ] = useState<string>();
	const iframeRef = useRef<HTMLIFrameElement>(null);
	const lastRequest = useRef(0);

	// useMemo: objeto estável enquanto as props não mudam, senão o debounce dispararia a cada render
	const current = useMemo(() => ({ title, blocks, joins, data, }), [ title, blocks, joins, data, ]);
	const input = useDebounce(current, 600);
	const hasData = Object.values(input.data).some((rows) => rows?.length);

	useEffect(() => {
		if (!input.blocks.length || !hasData) return;
		const requestId = ++lastRequest.current;
		postPreview({
			name: input.title,
			report: { blocks: input.blocks, joins: input.joins, },
			data: Object.fromEntries(Object.entries(input.data)
				.map(([ key, rows, ]) => [ key, (rows ?? []).slice(0, MAX_PREVIEW_ROWS), ])),
		})
			.unwrap()
			.then((res) => {
				if (requestId !== lastRequest.current) return;
				setHtml(res.payload.html);
				setError(undefined);
			})
			.catch((err) => {
				if (requestId !== lastRequest.current) return;
				setError(err?.data?.frontend_message ?? 'Não foi possível montar o preview.');
			});
	}, [ input, ]);

	// Altura do iframe acompanha o conteúdo (sem scripts dentro: só leitura do tamanho)
	const fitHeight = () => {
		const iframe = iframeRef.current;
		const height = iframe?.contentDocument?.documentElement.scrollHeight;
		if (iframe && height) iframe.style.height = `${height}px`;
	};

	let content: React.ReactNode;
	if (!blocks.length) content = <Empty description='Adicione um bloco para ver o relatório sendo montado.' />;
	else if (!hasData) content = <Empty description='Execute as consultas (▶) para ver o preview com a amostra do resultado.' />;
	else if (error && !html) content = <Typography.Text type='danger'>{error}</Typography.Text>;
	else content = (
		<iframe
			ref={iframeRef}
			title='Preview do relatório'
			sandbox='allow-same-origin'
			srcDoc={html}
			onLoad={fitHeight}
			style={{ width: '100%', minHeight: 320, border: 0, display: 'block', }}
		/>
	);

	return (
		<div>
			<Typography.Text strong>Preview do relatório</Typography.Text>
			<Typography.Paragraph type='secondary' style={{ fontSize: 12, marginBottom: 8, }}>
				Com a amostra do teste de SQL. No envio real entram os dados do momento da execução.
			</Typography.Paragraph>
			<Spin spinning={isLoading}>
				<div style={{ border: '1px solid rgba(0,0,0,0.1)', borderRadius: 8, overflow: 'hidden', minHeight: 120, }}>
					{content}
				</div>
			</Spin>
		</div>
	);
}
